import React, { useState, useEffect } from 'react';
import { Button } from './ui/button';
import { Card, CardContent } from './ui/card';
import { 
  Sparkles, Brain, FileText, Trophy, ArrowRight, ArrowLeft, 
  CheckCircle2, Lightbulb, Target, Rocket, X 
} from 'lucide-react';
import { ONBOARDING_COMPLETE_KEY } from '../constants/app';

const ONBOARDING_STEPS = [
  {
    id: 'welcome',
    title: 'Welcome to Idea Fountain! 🎉',
    description: 'Your AI-powered platform for developing breakthrough ideas and getting them evaluated.',
    icon: Sparkles,
    color: 'from-purple-500 to-pink-500'
  },
  {
    id: 'evaluate',
    title: 'Quick Evaluate',
    description: 'Upload any paper or idea to get instant AI-powered evaluation with detailed scoring across innovation metrics.',
    icon: FileText,
    color: 'from-blue-500 to-cyan-500',
    action: '/quick-evaluate'
  },
  {
    id: 'think',
    title: 'Think Lab',
    description: 'Use our guided thinking sessions to develop your ideas from scratch. AI prompts help you explore every angle.',
    icon: Brain,
    color: 'from-orange-500 to-yellow-500',
    action: '/session/new'
  },
  {
    id: 'track',
    title: 'Track Your Progress',
    description: 'All your evaluations and sessions are saved in "My Work". Watch your ideas evolve over time.',
    icon: Target,
    color: 'from-green-500 to-emerald-500',
    action: '/my-work'
  },
  {
    id: 'ready',
    title: 'You\'re Ready!',
    description: 'Start by evaluating an existing idea or create something new in Think Lab. The choice is yours!',
    icon: Rocket,
    color: 'from-purple-600 to-pink-600'
  }
];

const Onboarding = ({ onComplete, onSkip }) => {
  const [currentStep, setCurrentStep] = useState(0);
  const [isVisible, setIsVisible] = useState(true);

  const step = ONBOARDING_STEPS[currentStep];
  const isLastStep = currentStep === ONBOARDING_STEPS.length - 1;
  const isFirstStep = currentStep === 0;

  const handleNext = () => {
    if (isLastStep) {
      handleComplete();
    } else {
      setCurrentStep(prev => prev + 1);
    }
  };

  const handlePrev = () => {
    if (!isFirstStep) {
      setCurrentStep(prev => prev - 1);
    }
  };

  const handleComplete = () => {
    localStorage.setItem(ONBOARDING_COMPLETE_KEY, 'true');
    setIsVisible(false);
    if (onComplete) onComplete();
  };

  const handleSkip = () => {
    localStorage.setItem(ONBOARDING_COMPLETE_KEY, 'true');
    setIsVisible(false);
    if (onSkip) onSkip();
  };

  if (!isVisible) return null;

  const StepIcon = step.icon;

  return (
    <div className="fixed inset-0 bg-black/60 backdrop-blur-sm z-[200] flex items-center justify-center p-4">
      <Card className="w-full max-w-lg relative overflow-hidden">
        {/* Skip button */}
        <button
          onClick={handleSkip}
          className="absolute top-4 right-4 text-slate-400 hover:text-slate-600 z-10"
        >
          <X className="w-5 h-5" />
        </button>

        {/* Progress dots */}
        <div className="absolute top-4 left-1/2 -translate-x-1/2 flex gap-2">
          {ONBOARDING_STEPS.map((_, index) => (
            <div
              key={index}
              className={`w-2 h-2 rounded-full transition-all ${
                index === currentStep
                  ? 'bg-purple-600 w-6'
                  : index < currentStep
                  ? 'bg-purple-400'
                  : 'bg-slate-300'
              }`}
            />
          ))}
        </div>

        <CardContent className="pt-12 pb-6 px-6">
          {/* Icon */}
          <div className={`w-20 h-20 mx-auto mb-6 rounded-2xl bg-gradient-to-r ${step.color} flex items-center justify-center shadow-lg`}>
            <StepIcon className="w-10 h-10 text-white" />
          </div>

          {/* Content */}
          <h2 className="text-2xl font-bold text-center text-slate-900 mb-3">
            {step.title}
          </h2>
          <p className="text-center text-slate-600 mb-8">
            {step.description}
          </p>

          {/* Navigation */}
          <div className="flex items-center justify-between">
            <Button
              variant="ghost"
              onClick={handlePrev}
              disabled={isFirstStep}
              className={isFirstStep ? 'invisible' : ''}
            >
              <ArrowLeft className="w-4 h-4 mr-2" />
              Back
            </Button>

            <Button
              onClick={handleNext}
              className={`bg-gradient-to-r ${step.color} text-white`}
            >
              {isLastStep ? (
                <>
                  Get Started
                  <CheckCircle2 className="w-4 h-4 ml-2" />
                </>
              ) : (
                <>
                  Next
                  <ArrowRight className="w-4 h-4 ml-2" />
                </>
              )}
            </Button>
          </div>

          {/* Skip link */}
          <p className="text-center mt-4">
            <button
              onClick={handleSkip}
              className="text-sm text-slate-500 hover:text-slate-700 underline"
            >
              Skip tutorial
            </button>
          </p>
        </CardContent>
      </Card>
    </div>
  );
};

// Hook to check if onboarding should be shown
export const useOnboarding = () => {
  const [showOnboarding, setShowOnboarding] = useState(false);

  useEffect(() => {
    // Check if onboarding was already completed
    const completed = localStorage.getItem(ONBOARDING_COMPLETE_KEY);
    
    // Only show onboarding if:
    // 1. Not completed before
    // 2. Not on auth pages (login, signup, etc.)
    const isAuthPage = window.location.pathname.includes('/login') || 
                       window.location.pathname.includes('/signup') ||
                       window.location.pathname.includes('/auth') ||
                       window.location.pathname === '/';
    
    if (!completed && !isAuthPage) {
      setShowOnboarding(true);
    } else {
      setShowOnboarding(false);
    }
  }, []);

  const completeOnboarding = () => {
    localStorage.setItem(ONBOARDING_COMPLETE_KEY, 'true');
    setShowOnboarding(false);
  };

  const resetOnboarding = () => {
    localStorage.removeItem(ONBOARDING_COMPLETE_KEY);
    setShowOnboarding(true);
  };

  return { showOnboarding, completeOnboarding, resetOnboarding };
};

export default Onboarding;
